import { datadogLogs } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';
import type { AuthenticatedClient } from '@stitch-fix/client-facing-auth';
import { getWindow } from '@stitch-fix/mode-react';
import { getAuthenticatedClient, getVisitorUuid } from '@stitch-fix/client-facing-auth/browser';
import { hasLocalAssets } from '@stitch-fix/sf-next/settings';
import settings from '../../settings';
import { formatMessage, DEFAULT_LEVEL, type LoggerArgs } from './common';
export const initDatadogLogger = () => {
  const win = getWindow();
  if (!win) return;
  const clientToken = settings.datadogClientToken;
  if (!clientToken) return;
  const {
    client
  } = getAuthenticatedClient();
  const visitorId = getVisitorUuid();

  // Set user information for Datadog logs to match user set by CAR
  // source: https://github.com/stitchfix/web-frontend/blob/main/packages/client-analytics-reporter/src/init/index.ts
  if (client) {
    datadogLogs.setUser({
      id: client.clientId.toString(),
      business_line: client.businessLine,
      email: client.email,
      external_id: client.externalId,
      ...(visitorId && {
        visitor_id: visitorId
      })
    });
  } else if (visitorId) {
    datadogLogs.setUser({
      id: visitorId,
      visitor_id: visitorId
    });
  }
  datadogLogs.init({
    clientToken,
    env: hasLocalAssets() ? 'dev' : 'prod',
    forwardErrorsToLogs: false,
    service: `${settings.appName}-browser`,
    sessionSampleRate: 100,
    site: 'datadoghq.com',
    version: settings.appVersion
  });
};
type BrowserLoggerArgs = LoggerArgs & {
  /**
   * An optional JavaScript Error instance associated with the log entry
   * to enable Datadog to extract stack traces and error information.
   *
   * For developer convenience, the type is `unknown` to allow any value to be passed in.
   */
  error?: unknown;
};

/**
 * https://docs.datadoghq.com/logs/log_collection/javascript/#generic-logger-function
 * Logs can be found in Datadog under `service:growth-next-ui-browser`
 */
export const browserLogger = ({
  level = DEFAULT_LEVEL,
  message,
  team,
  feature,
  context = {},
  error
}: BrowserLoggerArgs) => {
  datadogLogs.logger.log(formatMessage(message), {
    team,
    feature,
    ...context
  }, level,
  // Datadog automatically extracts error information if the fourth parameter is an Error instance
  error && error instanceof Error ? error : undefined);
};

/**
 * Client-analytics-service adds the authenticated client's business line to the RUM context when it initializes Datadog RUM
 * with `initPerformanceAnalytics` in `instrumentation-client.ts`, [source](https://github.com/stitchfix/sf-next/blob/a76d2d7a2d447efe4fd6bef154fa2d7e565bf4a9/src/components/utils/analytics.ts#L56-L59)
 *
 * However, if they are unauthenticated and we would like to attribute RUM events to a specific business line,
 * such as the signup page, then we need to manually add the user's business line to the RUM context.
 * [docs](https://docs.datadoghq.com/real_user_monitoring/application_monitoring/browser/advanced_configuration/?tab=npm#add-override-user-session-property)
 */
export const addBusinessLineToRumContext = (businessLine: AuthenticatedClient['businessLine']) => {
  if (!businessLine) return;
  datadogRum.setUserProperty('business_line', businessLine);
};

// From docs: The following special characters are not supported for the Feature Flag name:
// ., :, +, -, =, &&, ||, >, <, !, (, ), {, }, [, ], ^, ", “, ”, ~, *, ?, \
const invalidFeatureFlagNameChars = /[ .:+\-=&&|><!()[\]{}^"“”~*?\\]/g;

/**
 * Enable feature flag tracking in Datadog RUM
 * [docs](https://docs.datadoghq.com/real_user_monitoring/feature_flag_tracking/using_feature_flags)
 */
export const addFeatureFlagsToRumContext = (featureFlags: {
  [featureFlagName: string]: boolean | string;
}) => {
  if (!datadogRum.addFeatureFlagEvaluation) return;
  for (const [name, value] of Object.entries(featureFlags)) {
    const sanitizedName = name.replace(invalidFeatureFlagNameChars, '_');
    datadogRum.addFeatureFlagEvaluation(sanitizedName, value);
  }
};

/**
 * Logging an error to console will be picked up by datadog logs
 */
export const logError = (error: unknown, ...params: unknown[]) => {
  // eslint-disable-next-line no-console
  console.error(error, ...params);
};